import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaWhatsapp,
  FaCamera,
  FaEnvelope,
  FaComments,
  FaGoogle,
  FaArrowRight,
  FaArrowLeft,
  FaCheck,
  FaStar,
  FaPhoneAlt,
  FaShieldAlt,
  FaLayerGroup,
  FaBullhorn,
} from "react-icons/fa";
import {
  LuCheck,
  LuSparkles,
  LuX,
  LuPhoneCall,
  LuSend,
} from "react-icons/lu";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import "../CSS/dm-editorial.css";

// ═══ DEFAULT FALLBACK DATA FOR OTHER SERVICES ═══
export const fallbackOtherServices = [
  {
    slug: "shoots",
    aliases: ["shoots-production", "shoot", "photography"],
    title: "Shoots",
    subtitle: "On-ground camera crew, audio & lighting production in Indore.",
    icon: "camera",
    badge: "On-Location in Indore",
    starting_price: "₹4,999 onwards",
    short_description:
      "Professional on-ground photoshoot, reel video shoot, podcast session, and 4K aerial drone coverage with high-end crew and lighting.",
    detailed_description:
      "Bring your brand to life with high-impact visual production. Our in-house creative production crew travels directly to your outlet, studio, office, or event location across Indore. Equipped with cinema-grade cameras, professional studio lighting, multi-track audio gear, and stabilized gimbals, we deliver ready-to-edit raw assets or finished viral content.",
    features: [
      {
        title: "Cinema-Grade Equipment",
        description:
          "Sony/Canon 4K full-frame bodies, prime lenses & gimbal stabilization for flawless production quality.",
      },
      {
        title: "Studio Lighting & Audio",
        description:
          "Multi-point softbox lighting, wireless lavalier microphones & professional field audio recording.",
      },
      {
        title: "Creative Direction & Hooks",
        description:
          "On-spot pose guidance, camera framing & viral reel hook direction by experienced directors.",
      },
      {
        title: "Fast Asset Handover",
        description:
          "Raw footage and selected high-resolution photos delivered via cloud link within 24–48 hours.",
      },
    ],
    packages: [
      {
        name: "Photoshoot Session",
        price: "₹4,999",
        unit: "per session",
        badge: "Most Popular",
        description:
          "Professional photo session for brand catalogs, ambience, food, fashion, or corporate headshots.",
        inclusions: [
          "Up to 2–3 hours on-ground session",
          "Professional lighting & prime lens kit",
          "1 location anywhere in Indore",
          "Full raw photo album + top curated shots",
          "Fast delivery within 24–48 hours",
        ],
      },
      {
        name: "Reel / Video Shoot Session",
        price: "₹7,499",
        unit: "per session",
        badge: "Creator Favorite",
        description:
          "Dynamic short-form video shooting optimized for Instagram Reels, YouTube Shorts, and Meta ads.",
        inclusions: [
          "Up to 2–3 hours video recording",
          "Direction for viral hooks & dialogue delivery",
          "4K 60fps slow-motion B-roll capture",
          "Wireless collar mic audio recording",
          "Organized footage delivery ready for editing",
        ],
      },
      {
        name: "Podcast Shoot Session",
        price: "₹9,999",
        unit: "per session",
        badge: "Studio Setup",
        description:
          "Broadcast-standard multi-camera podcast recording for founders, creators, and thought leaders.",
        inclusions: [
          "Up to 2–3 hours recording",
          "2-Camera multi-angle synchronized capture",
          "Studio-grade podcast mics & audio interface",
          "Clean audio mastering & noise reduction",
          "Separate audio & video stems delivery",
        ],
      },
      {
        name: "4K Drone Aerial Shoot",
        price: "₹7,499",
        unit: "per session",
        badge: "Cinematic",
        description:
          "Cinematic aerial video & wide-angle landscape shots for real estate, campus, and outdoor events.",
        inclusions: [
          "Up to 1–2 hours flight session",
          "Licensed professional drone operator",
          "4K high-bitrate aerial video & photos",
          "Smooth panoramic & tracking flight maneuvers",
          "Commercial aerial footage delivery",
        ],
      },
    ],
    faqs: [
      {
        question: "Where do the shoots take place?",
        answer:
          "Our crew travels to your location anywhere in Indore. Outstation shoots across MP can also be booked with prior travel arrangements.",
      },
      {
        question: "Is video/reel editing included?",
        answer:
          "The base shoot package covers the complete production crew, direction, and raw footage. Full reel editing with captions, sound effects, and grading can be easily added.",
      },
      {
        question: "What is the turnaround time for shoot delivery?",
        answer:
          "Raw assets are organized and uploaded to a private cloud folder within 24 to 48 hours of shoot completion.",
      },
    ],
  },
  {
    slug: "influencer",
    aliases: ["influencer-marketing", "influencers", "creators"],
    title: "Influencer",
    subtitle: "Creator collaborations & influencer marketing campaigns.",
    icon: "influencer",
    badge: "Verified Creators",
    starting_price: "Custom Plans",
    short_description:
      "Targeted collaborations with lifestyle, food, fashion, and business creators in Indore & PAN-India for authentic brand buzz.",
    detailed_description:
      "Leverage the trust and direct reach of authentic creators. Indorinama manages the entire influencer campaign lifecycle: from creator discovery, audience demographics vetting, and barter or commercial negotiations, to creative brief direction, content approvals, and post-campaign conversion analytics.",
    features: [
      {
        title: "Vetted Creator Network",
        description:
          "Over 500+ curated nano, micro, and macro influencers in Indore & nationwide across all niches.",
      },
      {
        title: "End-to-End Management",
        description:
          "Briefing, contract execution, sample gifting, shooting coordination, and delivery tracking.",
      },
      {
        title: "Hook & Script Guidelines",
        description:
          "We ensure creators communicate your unique value proposition naturally without sounding like an ad.",
      },
      {
        title: "Performance & ROI Analytics",
        description:
          "Detailed post-campaign reports on reel views, story taps, link clicks, and audience sentiment.",
      },
    ],
    packages: [
      {
        name: "Local Buzz (Nano & Micro)",
        price: "₹14,999 onwards",
        unit: "per campaign",
        badge: "Store Launches",
        description:
          "5 to 10 local Indore creators visiting your outlet or reviewing products for high local footfall.",
        inclusions: [
          "5–10 verified local creators",
          "Instagram Reels & Stories with location tags",
          "Collaborator co-author tagging",
          "Dedicated campaign supervisor",
          "Campaign reach & impression report",
        ],
      },
      {
        name: "Scale & Authority (Macro & Celeb)",
        price: "Custom Quote",
        unit: "per campaign",
        badge: "Brand Authority",
        description:
          "High-profile regional influencers and brand ambassadors for product launches and national brand positioning.",
        inclusions: [
          "Tier-1 creators (100k–1M+ followers)",
          "High-production collaborative reels",
          "Usage rights for paid ad amplification",
          "Exclusive category lock during campaign",
          "Guaranteed engagement benchmarks",
        ],
      },
    ],
    faqs: [
      {
        question: "How do you pick influencers for our brand?",
        answer:
          "We evaluate genuine engagement rate, audience demographics (age, city, gender), follower authenticity, and brand-category relevance to ensure you don't pay for bot followers.",
      },
      {
        question: "Can we review the content before creators publish?",
        answer:
          "Yes! Indorinama coordinates pre-approval of drafts, captions, and tags before any video or story goes live.",
      },
    ],
  },
  {
    slug: "whatsapp",
    aliases: ["whatsapp-marketing", "whatsapp-api"],
    title: "Whatsapp",
    subtitle: "Meta Official WhatsApp Cloud API & automated broadcast engines.",
    icon: "whatsapp",
    badge: "Official Meta API",
    starting_price: "₹0.40 / msg onwards",
    short_description:
      "Interactive broadcasts, abandoned cart recovery, automated support bots, and official Meta WhatsApp Business API integration.",
    detailed_description:
      "Reach your audience on their most frequently checked application. With 98% open rates, our WhatsApp marketing solutions help retail stores, cafes, clinics, and D2C brands drive direct sales, automate customer queries, send order confirmations, and run interactive broadcast campaigns without number ban risks.",
    features: [
      {
        title: "Official Meta Cloud API",
        description:
          "Zero ban risk, multi-agent inbox, and verified Green Tick badge application assistance.",
      },
      {
        title: "Interactive Templates",
        description:
          "Quick-reply buttons, call-to-action links, carousel product cards & multi-item catalogs.",
      },
      {
        title: "Smart Automated Chatbots",
        description:
          "Auto-reply to common questions, take appointment bookings, and route to human agents.",
      },
      {
        title: "High-Speed Delivery Engine",
        description:
          "Send thousands of personalized messages in minutes with detailed delivered & read analytics.",
      },
    ],
    packages: [
      {
        name: "Broadcast Campaign Starter",
        price: "₹2,499",
        unit: "setup + msg credits",
        badge: "Quick Launch",
        description:
          "Ideal for seasonal promotions, festive offers, and announcement blasts to your customer list.",
        inclusions: [
          "Meta template approval assistance",
          "Audience contact cleaning & upload",
          "Campaign execution & delivery analytics",
          "Button click & response tracking",
          "Opt-out & unsubscribe management",
        ],
      },
      {
        name: "WhatsApp Automation & CRM Bot",
        price: "₹6,999 / mo",
        unit: "monthly plan",
        badge: "Full Automation",
        description:
          "Full 24/7 automated conversational bot with CRM integrations and lead capture workflows.",
        inclusions: [
          "Custom conversational flow builder",
          "Shopify / WooCommerce / CRM webhook sync",
          "Abandoned cart & order status automation",
          "Multi-agent shared live team inbox",
          "Dedicated technical account manager",
        ],
      },
    ],
    faqs: [
      {
        question: "Will my phone number get blocked?",
        answer:
          "No! We use the Official Meta WhatsApp Business Cloud API with pre-approved templates, which completely avoids the risks of unofficial broadcast software.",
      },
      {
        question: "Can customers reply and chat with our team?",
        answer:
          "Yes. Replies enter a shared multi-agent inbox where your staff can chat directly with customers from desktop or mobile.",
      },
    ],
  },
  {
    slug: "mail",
    aliases: ["email-marketing", "email", "newsletters"],
    title: "Mail",
    subtitle: "High-converting newsletters & cold email outreach funnels.",
    icon: "mail",
    badge: "High Deliverability",
    starting_price: "₹3,499 / mo onwards",
    short_description:
      "High-converting newsletter design, cold B2B outreach engines, automated drip nurture sequences, and inbox placement setup.",
    detailed_description:
      "Build owned customer equity that algorithms can never take away. Email marketing generates superior conversion and retention ROI. We create custom-branded newsletters, automated onboarding funnels, transactional emails, and targeted B2B lead generation campaigns with rigorous SPF, DKIM, and DMARC technical setup.",
    features: [
      {
        title: "Responsive HTML Designs",
        description:
          "Pixel-perfect, mobile-first email designs that look stunning in Gmail, Apple Mail & Outlook.",
      },
      {
        title: "Automated Drip Sequences",
        description:
          "Welcome series, customer re-activation, post-purchase feedback, and educational nurture flows.",
      },
      {
        title: "Technical Inbox Deliverability",
        description:
          "Complete DNS configuration (SPF, DKIM, DMARC, MX) to land directly in the primary inbox.",
      },
      {
        title: "A/B Subject Line Testing",
        description:
          "Continuous optimization of open rates, click-through rates, and unsubscribes.",
      },
    ],
    packages: [
      {
        name: "Newsletter & Retention Retainer",
        price: "₹4,999 / mo",
        unit: "monthly retainer",
        badge: "Brand Loyalty",
        description:
          "Weekly curated branded newsletters that keep your brand top-of-mind and drive repeat orders.",
        inclusions: [
          "4 Custom branded newsletters per month",
          "Persuasive copywriting & graphic design",
          "List segmentation & audience cleanup",
          "Monthly open & click-rate reports",
          "Spam-score testing before send",
        ],
      },
      {
        name: "E-Commerce Email Funnel Automation",
        price: "₹8,999",
        unit: "one-time setup",
        badge: "Sales Driver",
        description:
          "Complete high-converting automated flows for online stores and service providers.",
        inclusions: [
          "3-Part Welcome series with discount hooks",
          "Abandoned checkout recovery series",
          "Customer review & referral drip flow",
          "Integration with Shopify / WooCommerce / Klaviyo",
          "Full revenue attribution dashboard",
        ],
      },
    ],
    faqs: [
      {
        question: "How do you prevent emails from landing in spam?",
        answer:
          "We authenticate domain DNS records (SPF, DKIM, DMARC), warm up domain reputation gradually, and use clean HTML templates compliant with anti-spam standards.",
      },
      {
        question: "Can you design custom graphics for our newsletters?",
        answer:
          "Yes! Every newsletter is designed with custom graphics, product showcases, and clear call-to-action buttons tailored to your brand style.",
      },
    ],
  },
  {
    slug: "sms",
    aliases: ["sms-marketing", "bulk-sms", "text-marketing"],
    title: "SMS",
    subtitle: "DLT approved promotional bursts & transactional OTP delivery.",
    icon: "sms",
    badge: "DLT Approved Routes",
    starting_price: "₹0.18 / SMS onwards",
    short_description:
      "High-priority transactional alerts, instant OTP routing, promotional flash-sale bursts, and 100% DLT compliance assistance.",
    detailed_description:
      "Deliver critical messages directly into customers pockets within seconds. Ideal for urgent flash sales, discount coupons, order status, and instant OTP verification. We manage the entire telecom DLT onboarding, template approval, and high-throughput operator routing.",
    features: [
      {
        title: "Tier-1 Operator Routes",
        description:
          "Direct interconnect with major telecom operators for sub-5 second delivery speed.",
      },
      {
        title: "DLT Registration Support",
        description:
          "Guaranteed help registering your entity, sender headers (e.g. INDNAM), and SMS templates.",
      },
      {
        title: "Promotional & Transactional Split",
        description:
          "Separate reliable routes for high-volume marketing blasts and mission-critical OTPs.",
      },
      {
        title: "Real-Time Delivery Logs",
        description:
          "Live delivery reports, failure fallback tracking, and link-click analytics.",
      },
    ],
    packages: [
      {
        name: "Promotional Blast Pack",
        price: "₹1,999 onwards",
        unit: "per pack",
        badge: "Flash Sales",
        description:
          "High-speed SMS broadcasts to drive footfalls to your store, exhibition, or weekend sale.",
        inclusions: [
          "10,000+ Promotional SMS credits",
          "Assistance with DLT template approval",
          "Shortlink tracking & delivery report",
          "Fast high-throughput delivery",
          "Scheduled send capability",
        ],
      },
      {
        name: "Transactional & OTP Gateway",
        price: "₹3,499 onwards",
        unit: "credits pack",
        badge: "High Priority",
        description:
          "24/7 priority routing for verification codes, password resets, and order confirmations.",
        inclusions: [
          "Under 5-second delivery guarantee",
          "REST API integration for developers",
          "High uptime SLA & failover routing",
          "Real-time webhook notifications",
          "Full technical documentation",
        ],
      },
    ],
    faqs: [
      {
        question: "What is DLT registration?",
        answer:
          "TRAI mandates DLT registration for all commercial SMS senders in India. Our team will guide and manage the registration process on Jio / Airtel / Vi DLT portals for you.",
      },
      {
        question: "Can we have our brand name as the sender ID?",
        answer:
          "Yes! We help you get approved for a 6-character alphabetic sender header (e.g. INDNAM) on DLT.",
      },
    ],
  },
  {
    slug: "google-data-extractor",
    aliases: ["data-extractor", "b2b-leads", "google-extractor"],
    title: "Google Data Extractor",
    subtitle: "Targeted B2B contact lists & verified directory data.",
    icon: "google_data",
    badge: "Verified B2B Leads",
    starting_price: "₹1,999 onwards",
    short_description:
      "Extract verified business phone numbers, emails, addresses, and ratings from Google Maps & directories for targeted sales outreach.",
    detailed_description:
      "Arm your outbound sales team with hyper-targeted B2B leads. Our Google Data Extractor technology systematically gathers and cleans verified contact details of local businesses across any city and industry (e.g., Doctors, Real Estate Builders, Cafes, Salons, Manufacturers).",
    features: [
      {
        title: "City & Pincode Precision",
        description:
          "Target leads strictly in Indore, Madhya Pradesh, or any specific district in India.",
      },
      {
        title: "Rich Data Fields",
        description:
          "Business Name, Category, Verified Phone Numbers, Email, Full Address, Website, and Google Rating.",
      },
      {
        title: "Clean CSV/Excel Export",
        description:
          "Ready-to-use spreadsheets formatted for cold calling, WhatsApp marketing, or CRM import.",
      },
      {
        title: "Verified Active Numbers",
        description:
          "Filtered for active contacts to save your team countless hours of wasted dialing.",
      },
    ],
    packages: [
      {
        name: "City Starter Pack (5,000 Leads)",
        price: "₹1,999",
        unit: "one-time export",
        badge: "Local Outreach",
        description:
          "Up to 5,000 targeted business leads in Indore across up to 3 chosen categories.",
        inclusions: [
          "5,000 Verified B2B leads",
          "Filtered by industry category & city",
          "Excel / CSV spreadsheet format",
          "Includes phone, address & category",
          "Delivered within 24 hours",
        ],
      },
      {
        name: "Growth Lead Pack (20,000 Leads)",
        price: "₹4,999",
        unit: "one-time export",
        badge: "Best Value",
        description:
          "Comprehensive lead database across multiple cities or entire state sectors.",
        inclusions: [
          "20,000 Verified business contacts",
          "Includes phone, address, website & ratings",
          "Multi-category & multi-city selection",
          "Free WhatsApp broadcast formatting",
          "Deduplicated clean dataset",
        ],
      },
    ],
    faqs: [
      {
        question: "What fields are provided in the extracted file?",
        answer:
          "You receive Business Name, Category, Phone Number, Full Address, Website URL, Rating, and Review count in clean CSV/Excel format.",
      },
      {
        question: "Is this data legal and compliant?",
        answer:
          "Yes, our tools only extract publicly available directory information listed directly on Google Maps and public business directories.",
      },
    ],
  },
  {
    slug: "static-website",
    aliases: ["static-website", "website", "static-site", "business-website"],
    title: "Static Website",
    subtitle: "Fast, SEO-ready business websites that convert visitors into enquiries.",
    icon: "website",
    badge: "SEO Ready",
    starting_price: "₹9,999 onwards",
    short_description:
      "Lightning-fast static business websites with modern design, WhatsApp enquiry integration and Google Maps lead-ready structure.",
    detailed_description:
      "Get a professional online presence that loads instantly and ranks on Google. Our static websites are hand-coded for speed, mobile-first design, on-page SEO, contact enquiry forms, WhatsApp click-to-chat and Google Business integration — perfect for local businesses, clinics, cafes, realtors and service providers.",
    features: [
      {
        title: "Mobile-First Modern Design",
        description:
          "Clean, premium 5–8 page business website crafted to match your brand colours and style.",
      },
      {
        title: "Speed & SEO Optimised",
        description:
          "Static build for sub-second loading with meta tags, sitemap, schema and Google Search Console setup.",
      },
      {
        title: "Enquiry & WhatsApp Integration",
        description:
          "Click-to-call, WhatsApp chat, enquiry forms and Google Maps embed to capture every lead.",
      },
      {
        title: "Launch & Handover Support",
        description:
          "Domain connection assistance, hosting guidance and 15-day post-launch edit support.",
      },
    ],
    packages: [
      {
        name: "Business Starter Website",
        price: "₹9,999",
        unit: "one-time",
        badge: "Quick Launch",
        description:
          "5-page static business website ideal for local shops, clinics, cafes and service providers.",
        inclusions: [
          "Up to 5 pages (Home, About, Services, Gallery, Contact)",
          "Mobile-first responsive design",
          "WhatsApp chat + enquiry form",
          "Basic on-page SEO & Google Maps embed",
          "Delivery within 7 days",
        ],
      },
      {
        name: "Business Growth Website",
        price: "₹19,999",
        unit: "one-time",
        badge: "Best Value",
        description:
          "Up to 10 pages with blog-ready structure, advanced SEO and conversion-focused copy assistance.",
        inclusions: [
          "Up to 10 pages + blog setup",
          "Advanced on-page SEO & schema",
          "Speed optimisation & analytics setup",
          "Social & Google Business integration",
          "30-day post-launch support",
        ],
      },
    ],
    faqs: [
      {
        question: "Will my website rank on Google?",
        answer:
          "Yes, every static website ships with on-page SEO, sitemap, schema and Search Console setup so Google can index and rank your business pages.",
      },
      {
        question: "Can customers enquire directly from the website?",
        answer:
          "Yes. Enquiry forms, click-to-call buttons and WhatsApp chat are built in so every visitor can contact you instantly.",
      },
    ],
  },
];

// Helper to render icon for services
const ServiceIcon = ({ iconKey, className = "text-xl" }) => {
  switch (iconKey) {
    case "camera":
    case "shoots":
      return <FaCamera className={className} />;
    case "influencer":
      return <FaStar className={className} />;
    case "whatsapp":
      return <FaWhatsapp className={className} />;
    case "mail":
    case "email":
      return <FaEnvelope className={className} />;
    case "sms":
      return <FaComments className={className} />;
    case "google_data":
    case "data_extractor":
    case "search":
      return <FaGoogle className={className} />;
    case "website":
    case "static-website":
    case "static":
      return <FaBullhorn className={className} />;
    default:
      return <FaLayerGroup className={className} />;
  }
};

// Format numbers in Indian number format (e.g., 10,000, 1,00,000)
const formatRupees = (amount) => {
  const num = Math.round(Number(amount) || 0);
  return num.toLocaleString("en-IN");
};

// Volume tiers & calculation configurations for volume-based marketing services
const serviceVolumeConfigs = {
  whatsapp: {
    unit: "Messages",
    label: "WhatsApp Messages",
    min: 5000,
    max: 100000,
    step: 5000,
    defaultQty: 10000,
    presets: [5000, 10000, 25000, 50000, 100000],
    tiers: [
      { qty: 5000, price: 2499 },
      { qty: 10000, price: 4499 },
      { qty: 25000, price: 9999 },
      { qty: 50000, price: 18499 },
      { qty: 100000, price: 34999 },
    ],
    rateNote: "Official Meta Cloud API · High delivery guarantee",
  },
  sms: {
    unit: "SMS Credits",
    label: "Promotional / OTP SMS",
    min: 5000,
    max: 200000,
    step: 5000,
    defaultQty: 10000,
    presets: [10000, 25000, 50000, 100000, 200000],
    tiers: [
      { qty: 5000, price: 1199 },
      { qty: 10000, price: 1999 },
      { qty: 25000, price: 4750 },
      { qty: 50000, price: 9000 },
      { qty: 100000, price: 17000 },
      { qty: 200000, price: 32000 },
    ],
    rateNote: "DLT Approved Routes · Instant delivery reports",
  },
  mail: {
    unit: "Emails / Mo",
    label: "Email Newsletters & Outreach",
    min: 2500,
    max: 100000,
    step: 2500,
    defaultQty: 10000,
    presets: [5000, 10000, 25000, 50000, 100000],
    tiers: [
      { qty: 2500, price: 2499 },
      { qty: 5000, price: 3499 },
      { qty: 10000, price: 4999 },
      { qty: 25000, price: 8999 },
      { qty: 50000, price: 14999 },
      { qty: 100000, price: 24999 },
    ],
    rateNote: "Dedicated IP warmup · High inbox deliverability",
  },
  "google-data-extractor": {
    unit: "B2B Leads",
    label: "Verified B2B Leads",
    min: 2500,
    max: 50000,
    step: 2500,
    defaultQty: 5000,
    presets: [2500, 5000, 10000, 20000, 50000],
    tiers: [
      { qty: 2500, price: 1299 },
      { qty: 5000, price: 1999 },
      { qty: 10000, price: 3499 },
      { qty: 20000, price: 4999 },
      { qty: 50000, price: 9999 },
    ],
    rateNote: "Google Maps directory data · Clean Excel/CSV",
  },
  shoots: {
    unit: "Shoot Sessions",
    label: "On-Ground Shoot Sessions",
    min: 1,
    max: 10,
    step: 1,
    defaultQty: 1,
    presets: [1, 2, 3, 5, 8],
    tiers: [
      { qty: 1, price: 4999 },
      { qty: 2, price: 8999 },
      { qty: 3, price: 12999 },
      { qty: 4, price: 16999 },
      { qty: 5, price: 19999 },
      { qty: 8, price: 29999 },
      { qty: 10, price: 36999 },
    ],
    rateNote: "On-ground camera crew in Indore · Raw 4K footage delivered",
  },
};

const interpolateTier = (val, tiers) => {
  if (!tiers || tiers.length === 0) return 0;
  if (val <= tiers[0].qty) return tiers[0].price;
  if (val >= tiers[tiers.length - 1].qty) return tiers[tiers.length - 1].price;
  for (let i = 0; i < tiers.length - 1; i++) {
    const t1 = tiers[i];
    const t2 = tiers[i + 1];
    if (val >= t1.qty && val <= t2.qty) {
      const ratio = (val - t1.qty) / (t2.qty - t1.qty);
      return Math.round(t1.price + ratio * (t2.price - t1.price));
    }
  }
  return tiers[0].price;
};

const getNormalizedServiceKey = (slug) => {
  const s = (slug || "").toLowerCase().trim();
  if (s.includes("whatsapp")) return "whatsapp";
  if (s.includes("sms")) return "sms";
  if (s.includes("mail") || s.includes("email") || s.includes("newsletter")) return "mail";
  if (s.includes("extractor") || s.includes("data") || s.includes("lead")) return "google-data-extractor";
  if (s.includes("shoot") || s.includes("camera") || s.includes("photo")) return "shoots";
  if (s.includes("website") || s.includes("web") || s.includes("static")) return "static-website";
  return s;
};

// 4 Contextually relevant services for each service page
const relevantServicesMap = {
  whatsapp: ["sms", "mail", "google-data-extractor", "static-website"],
  sms: ["whatsapp", "mail", "google-data-extractor", "static-website"],
  mail: ["whatsapp", "google-data-extractor", "static-website", "sms"],
  "google-data-extractor": ["whatsapp", "mail", "sms", "static-website"],
  shoots: ["influencer", "static-website", "whatsapp", "mail"],
  "static-website": ["whatsapp", "shoots", "google-data-extractor", "mail"],
  influencer: ["shoots", "static-website", "whatsapp", "mail"],
};

const DMServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Fast initialization from sessionStorage cache if already fetched
  const [servicesData, setServicesData] = useState(() => {
    try {
      const cached = sessionStorage.getItem("indorinama_dm_services");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return fallbackOtherServices.map((fb) => {
            const live = parsed.find(
              (s) => s.slug === fb.slug || (fb.aliases && fb.aliases.includes(s.slug))
            );
            return live ? { ...fb, ...live } : fb;
          });
        }
      }
    } catch (_) {}
    return fallbackOtherServices;
  });

  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    brandName: "",
    notes: "",
  });

  // Fetch ONLY what is needed for this page (targeted endpoints & lightweight depth=0)
  useEffect(() => {
    let isMounted = true;
    const fetchServiceData = async () => {
      const cleanSlug = (slug || "").toLowerCase().trim();
      const baseUrl = process.env.REACT_APP_PAYLOAD_URL || "http://localhost:8000";
      const prodBase = "https://admin.indorinama.in";

      // Prioritize targeted endpoint (only current service or only services catalog)
      // NOTE: prodBase must stay — production builds can't reach localhost,
      // otherwise the page falls back to stale hardcoded service data.
      const candidateEndpoints = [
        `${baseUrl}/api/globals/digital-marketing/services/${cleanSlug}`,
        `${prodBase}/api/globals/digital-marketing/services/${cleanSlug}`,
        `/api/globals/digital-marketing/services/${cleanSlug}`,
        `${baseUrl}/api/globals/digital-marketing/services`,
        `${prodBase}/api/globals/digital-marketing/services`,
        `/api/globals/digital-marketing/services`,
        `${baseUrl}/api/globals/digital-marketing?depth=0`,
        `${prodBase}/api/globals/digital-marketing?depth=0`,
        `/api/globals/digital-marketing?depth=0`,
      ];

      for (const ep of candidateEndpoints) {
        try {
          const res = await fetch(ep, { headers: { Accept: "application/json" } });
          if (res.ok) {
            const json = await res.json();
            if (isMounted) {
              if (Array.isArray(json) && json.length > 0) {
                // Array of services
                const merged = fallbackOtherServices.map((fb) => {
                  const live = json.find(
                    (s) => s.slug === fb.slug || (fb.aliases && fb.aliases.includes(s.slug))
                  );
                  return live ? { ...fb, ...live } : fb;
                });
                setServicesData(merged);
                try {
                  sessionStorage.setItem("indorinama_dm_services", JSON.stringify(json));
                } catch (_) {}
                return;
              } else if (json && json.slug) {
                // Single targeted service
                setServicesData((prev) =>
                  prev.map((fb) =>
                    fb.slug === json.slug || (fb.aliases && fb.aliases.includes(json.slug))
                      ? { ...fb, ...json }
                      : fb
                  )
                );
                return;
              } else if (json && json.other_services && Array.isArray(json.other_services)) {
                // Full global fallback
                const merged = fallbackOtherServices.map((fb) => {
                  const live = json.other_services.find(
                    (s) => s.slug === fb.slug || (fb.aliases && fb.aliases.includes(s.slug))
                  );
                  return live ? { ...fb, ...live } : fb;
                });
                setServicesData(merged);
                try {
                  sessionStorage.setItem(
                    "indorinama_dm_services",
                    JSON.stringify(json.other_services)
                  );
                } catch (_) {}
                return;
              }
            }
          }
        } catch (_) {
          // Try next endpoint
        }
      }
    };

    fetchServiceData();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  const cleanSlug = (slug || "").toLowerCase().trim();
  if (cleanSlug === "influencer" || cleanSlug === "influencer-marketing") {
    return <Navigate to="/influencer-marketing" replace />;
  }

  // Find the active service
  const currentService = useMemo(() => {
    return (
      servicesData.find(
        (s) => s.slug === cleanSlug || (s.aliases && s.aliases.includes(cleanSlug))
      ) || servicesData[0]
    );
  }, [slug, servicesData]);

  const normalizedKey = useMemo(() => {
    return getNormalizedServiceKey(currentService.slug);
  }, [currentService]);

  const isWebsiteService = normalizedKey === "static-website";
  const activeVolumeConfig = serviceVolumeConfigs[normalizedKey] || null;

  const [volumeQty, setVolumeQty] = useState(10000);
  const [selectedWebsitePkgIndex, setSelectedWebsitePkgIndex] = useState(0);

  // Sync volumeQty with defaultQty whenever active service changes
  useEffect(() => {
    if (activeVolumeConfig) {
      setVolumeQty(activeVolumeConfig.defaultQty);
    }
  }, [normalizedKey]);

  // Website packages for static website service (2 packages)
  const websitePackages = useMemo(() => {
    if (currentService.packages && currentService.packages.length > 0) {
      return currentService.packages;
    }
    const fb = fallbackOtherServices.find((s) => s.slug === "static-website");
    return fb?.packages || [];
  }, [currentService]);

  // Calculated investment based on volume slider
  const calculatedPrice = useMemo(() => {
    if (!activeVolumeConfig) return 0;
    return interpolateTier(volumeQty, activeVolumeConfig.tiers);
  }, [activeVolumeConfig, volumeQty]);

  // 4 Relevant other services for bottom cross-navigation
  const otherServicesList = useMemo(() => {
    const clean = (currentService.slug || "").toLowerCase().trim();
    const normalized = getNormalizedServiceKey(clean);
    const relevantSlugs = relevantServicesMap[normalized] || relevantServicesMap[clean];

    if (relevantSlugs && relevantSlugs.length > 0) {
      const mapped = relevantSlugs
        .map((rSlug) =>
          servicesData.find(
            (s) => s.slug === rSlug || (s.aliases && s.aliases.includes(rSlug))
          )
        )
        .filter(Boolean);

      if (mapped.length > 0) {
        const remaining = servicesData.filter(
          (s) => s.slug !== currentService.slug && !mapped.some((m) => m.slug === s.slug)
        );
        return [...mapped, ...remaining].slice(0, 4);
      }
    }

    return servicesData
      .filter((s) => s.slug !== currentService.slug)
      .slice(0, 4);
  }, [servicesData, currentService]);

  // WhatsApp click handler for static packages
  const handleWhatsApp = (packageName = "") => {
    const text = `Hi Indorinama! I'm interested in your *${currentService.title}* service${
      packageName ? ` (Package: ${packageName})` : ""
    }. Please share details and availability.`;
    const url = `https://wa.me/917987258410?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  // WhatsApp click handler for volume & live calculated price
  const handleWhatsAppVolume = () => {
    if (activeVolumeConfig) {
      const text = `Hi Indorinama! I'm interested in your *${currentService.title}* service for *${formatRupees(
        volumeQty
      )} ${activeVolumeConfig.unit}* (Est. Investment: ₹${formatRupees(calculatedPrice)}). Please share timeline and get started.`;
      const url = `https://wa.me/917987258410?text=${encodeURIComponent(text)}`;
      window.open(url, "_blank");
    } else if (isWebsiteService && websitePackages[selectedWebsitePkgIndex]) {
      handleWhatsApp(websitePackages[selectedWebsitePkgIndex]?.name);
    } else {
      handleWhatsApp();
    }
  };

  // Form submit handler
  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const packageInfo =
        selectedPackage?.name ||
        (activeVolumeConfig
          ? `${currentService.title} (${formatRupees(volumeQty)} ${activeVolumeConfig.unit} - Est: ₹${formatRupees(calculatedPrice)})`
          : isWebsiteService
          ? websitePackages[selectedWebsitePkgIndex]?.name
          : "General Service Inquiry");

      await fetch("/api/service-inquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          serviceSlug: currentService.slug,
          serviceTitle: currentService.title,
          packageName: packageInfo,
          volume: activeVolumeConfig ? volumeQty : undefined,
          estimatedPrice: activeVolumeConfig ? calculatedPrice : undefined,
          ...formData,
        }),
      }).catch(() => null);

      setSubmitted(true);
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    } catch (err) {
      setSubmitted(true);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5efe8] text-[#161310] font-sans selection:bg-[#bc1535] selection:text-white">
      <SEO
        title={`${currentService.title} - Digital Marketing Services | Indorinama`}
        description={currentService.short_description}
      />

      <Navbar />

      {/* ═══ BREADCRUMBS & TOP NAV BAR ═══ */}
      <div className="border-b border-[#161310]/10 bg-white/70 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-6 lg:px-8 text-xs">
          <div className="flex items-center gap-2 text-[#161310]/60">
            <Link to="/" className="text-[#161310] hover:text-[#bc1535] transition font-medium no-underline">
              Home
            </Link>
            <span>/</span>
            <Link to="/digital-marketing" className="text-[#161310] hover:text-[#bc1535] transition font-medium no-underline">
              Digital Marketing
            </Link>
            <span>/</span>
            <span className="font-semibold text-[#bc1535]">{currentService.title}</span>
          </div>

          <Link
            to="/digital-marketing"
            className="flex items-center gap-1.5 font-bold text-[#161310] hover:text-[#bc1535] transition no-underline"
          >
            <FaArrowLeft className="text-[10px]" />
            <span>Back to DM Plans</span>
          </Link>
        </div>
      </div>

      {/* ═══ HERO SECTION ═══ */}
      <section className="relative overflow-hidden pt-6 pb-8 sm:pt-8 sm:pb-10 border-b border-[#161310]/10">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 h-80 w-80 rounded-full bg-[#bc1535]/5 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 h-80 w-80 rounded-full bg-amber-500/5 blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-3">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 rounded-full border border-[#bc1535]/20 bg-[#bc1535]/5 px-3 py-1 text-xs font-semibold text-[#bc1535]">
                <ServiceIcon iconKey={currentService.icon} className="text-sm" />
                <span>{currentService.badge || "Professional Service"}</span>
                {currentService.starting_price && (
                  <span className="ml-1 rounded-full bg-[#bc1535] px-2 py-0.5 text-[10px] font-bold text-white">
                    {currentService.starting_price}
                  </span>
                )}
              </div>

              {/* Headline */}
              <h1 className="dm-serif text-3xl font-light tracking-tight text-[#161310] sm:text-4xl md:text-5xl lg:leading-[1.15]">
                {currentService.title}{" "}
                <em className="font-normal italic text-[#bc1535]">Solutions</em>
              </h1>

              {/* Subtitle */}
              <p className="text-sm sm:text-base font-normal leading-relaxed text-[#161310]/75 max-w-2xl">
                {currentService.detailed_description || currentService.short_description}
              </p>

              {/* Action Buttons */}
              <div className="pt-1 flex flex-wrap items-center gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => {
                    if (activeVolumeConfig) {
                      setSelectedPackage({
                        name: `${currentService.title} (${formatRupees(volumeQty)} ${activeVolumeConfig.unit})`,
                        price: `₹${formatRupees(calculatedPrice)}`,
                      });
                    } else if (isWebsiteService && websitePackages[selectedWebsitePkgIndex]) {
                      setSelectedPackage(websitePackages[selectedWebsitePkgIndex]);
                    } else {
                      setSelectedPackage(null);
                    }
                    setInquiryModalOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-2xl bg-[#bc1535] px-6 py-3 text-xs sm:text-sm font-bold text-white shadow-md hover:bg-[#a10f2b] transition hover:shadow-lg"
                >
                  <LuPhoneCall className="text-base" />
                  <span>Request Callback &amp; Quote</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (activeVolumeConfig) {
                      handleWhatsAppVolume();
                    } else if (isWebsiteService && websitePackages[selectedWebsitePkgIndex]) {
                      handleWhatsApp(websitePackages[selectedWebsitePkgIndex]?.name);
                    } else {
                      handleWhatsApp();
                    }
                  }}
                  className="flex items-center gap-2 rounded-2xl border border-[#161310]/15 bg-white px-5 py-3 text-xs sm:text-sm font-semibold text-[#161310] hover:bg-[#fcfaf7] hover:border-[#161310]/30 transition shadow-sm"
                >
                  <FaWhatsapp className="text-emerald-600 text-base" />
                  <span>Chat on WhatsApp</span>
                </button>
              </div>

              {/* Key Trust Signals */}
              <div className="pt-2 flex flex-wrap items-center gap-4 sm:gap-6 text-xs text-[#161310]/70">
                <div className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <LuCheck className="text-[10px]" />
                  </span>
                  <span>100% In-House Team</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <LuCheck className="text-[10px]" />
                  </span>
                  <span>Rapid 24–48h Turnaround</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <LuCheck className="text-[10px]" />
                  </span>
                  <span>Transparent Pricing</span>
                </div>
              </div>
            </div>

            {/* Right Card / Visual Snapshot & Volume/Package Calculator */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-[#161310] bg-[#161310] p-4 sm:p-5 shadow-2xl relative overflow-hidden text-white">
                <div className="absolute top-0 right-0 h-40 w-40 -mr-10 -mt-10 rounded-full bg-[#bc1535]/30 blur-3xl pointer-events-none" />

                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#bc1535] text-white shadow-md">
                      <ServiceIcon iconKey={currentService.icon} className="text-xl" />
                    </div>
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#ff8ca0]">
                        {isWebsiteService ? "Website Packages" : "Volume & Pricing Calculator"}
                      </span>
                      <h3 className="font-bold text-white text-base sm:text-lg">{currentService.title}</h3>
                    </div>
                  </div>
                  {isWebsiteService && (
                    <span className="rounded-full bg-emerald-500/20 border border-emerald-400/30 px-2.5 py-0.5 text-[11px] font-bold text-emerald-300">2 Fixed Tiers</span>
                  )}
                </div>

                {isWebsiteService ? (
                  /* Website Development (2 Packages) */
                  <div className="mt-3.5 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      {websitePackages.map((pkg, idx) => {
                        const isSelected = selectedWebsitePkgIndex === idx;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setSelectedWebsitePkgIndex(idx)}
                            className={`rounded-xl p-3 text-left transition-all border ${
                              isSelected
                                ? "border-[#bc1535] bg-[#bc1535]/20 ring-1 ring-[#bc1535]"
                                : "border-white/10 bg-white/5 hover:bg-white/10"
                            }`}
                          >
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#ff8ca0] block">
                              {pkg.badge || `Option ${idx + 1}`}
                            </span>
                            <p className="font-bold text-xs text-white mt-0.5 line-clamp-1">{pkg.name}</p>
                            <p className="text-base font-extrabold text-[#ff8ca0] mt-1">{pkg.price}</p>
                          </button>
                        );
                      })}
                    </div>

                    <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-white/60">Selected Package:</span>
                        <span className="font-bold text-white">
                          {websitePackages[selectedWebsitePkgIndex]?.name}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-white/60">Investment:</span>
                        <span className="font-extrabold text-base text-[#ff8ca0]">
                          {websitePackages[selectedWebsitePkgIndex]?.price}
                        </span>
                      </div>
                      <p className="text-[11px] text-white/60 leading-relaxed pt-1 border-t border-white/10">
                        {websitePackages[selectedWebsitePkgIndex]?.description}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleWhatsApp(websitePackages[selectedWebsitePkgIndex]?.name)}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-xs sm:text-sm font-bold text-white shadow-md hover:bg-emerald-500 transition"
                    >
                      <FaWhatsapp className="text-base" />
                      <span>Talk Directly with our Specialist</span>
                    </button>
                  </div>
                ) : activeVolumeConfig ? (
                  /* Volume Slider for SMS, WhatsApp, Email, Data Extractor, Shoots */
                  <div className="mt-3.5 space-y-3">
                    {/* Header + Number Input */}
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/60 block">
                          {activeVolumeConfig.label}
                        </span>
                        <div className="flex items-baseline gap-1 mt-0.5">
                          <span className="text-xl sm:text-2xl font-black text-white">
                            {formatRupees(volumeQty)}
                          </span>
                          <span className="text-xs text-white/60 font-medium">
                            {activeVolumeConfig.unit}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-white/50">Edit:</span>
                        <input
                          type="number"
                          min={activeVolumeConfig.min}
                          max={activeVolumeConfig.max}
                          step={activeVolumeConfig.step}
                          value={volumeQty}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            if (!isNaN(val)) {
                              setVolumeQty(Math.max(activeVolumeConfig.min, Math.min(activeVolumeConfig.max, val)));
                            }
                          }}
                          className="w-24 rounded-lg bg-white px-2.5 py-1 text-right text-xs font-bold text-[#161310] outline-none shadow-sm focus:ring-2 focus:ring-[#bc1535]"
                        />
                      </div>
                    </div>

                    {/* Quick Presets (White bg, black text, red when selected) */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-[10px] text-white/60">
                        <span>Quick Presets:</span>
                        <span className="text-white/40">Range: {formatRupees(activeVolumeConfig.min)} – {formatRupees(activeVolumeConfig.max)}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {activeVolumeConfig.presets.map((preset) => {
                          const isSelected = volumeQty === preset;
                          return (
                            <button
                              key={preset}
                              type="button"
                              onClick={() => setVolumeQty(preset)}
                              className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition-all shadow-sm ${
                                isSelected
                                  ? "border border-white/40 bg-[#bc1535] text-white ring-1 ring-white/30"
                                  : "border border-transparent bg-white text-[#161310] hover:bg-white/90"
                              }`}
                            >
                              {formatRupees(preset)} {activeVolumeConfig.unit === "Shoot Sessions" ? (preset === 1 ? "Session" : "Sessions") : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Range Slider */}
                    <div className="pt-1">
                      {(() => {
                        const rangeSpan = activeVolumeConfig.max - activeVolumeConfig.min;
                        const progress = rangeSpan > 0 ? ((volumeQty - activeVolumeConfig.min) / rangeSpan) * 100 : 0;
                        return (
                          <input
                            type="range"
                            min={activeVolumeConfig.min}
                            max={activeVolumeConfig.max}
                            step={activeVolumeConfig.step}
                            value={volumeQty}
                            onChange={(e) => setVolumeQty(Number(e.target.value))}
                            aria-label={`Adjust ${activeVolumeConfig.label}`}
                            className="dm-compact-slider dm-white-slider"
                            style={{
                              background: `linear-gradient(to right, #bc1535 0%, #bc1535 ${progress}%, #ffffff ${progress}%, #ffffff 100%)`,
                            }}
                          />
                        );
                      })()}
                      <div className="flex justify-between text-[10px] text-white/40 mt-1 font-mono">
                        <span>{formatRupees(activeVolumeConfig.min)}</span>
                        <span>{formatRupees(activeVolumeConfig.max)}</span>
                      </div>
                    </div>

                    {/* Calculated Investment Card */}
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white/60 block">
                          Estimated Investment
                        </span>
                        <div className="flex items-baseline gap-1.5 mt-0.5">
                          <span className="dm-serif text-2xl sm:text-3xl font-bold text-[#ff8ca0]">
                            ₹{formatRupees(calculatedPrice)}
                          </span>
                          <span className="text-[10px] text-white/50">
                            (+ 18% GST)
                          </span>
                        </div>
                        <p className="text-[10px] text-emerald-400 mt-0.5">
                          {activeVolumeConfig.rateNote}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPackage({
                            name: `${currentService.title} (${formatRupees(volumeQty)} ${activeVolumeConfig.unit})`,
                            price: `₹${formatRupees(calculatedPrice)}`,
                          });
                          setInquiryModalOpen(true);
                        }}
                        className="shrink-0 rounded-lg bg-white/10 hover:bg-white/20 border border-white/15 px-2.5 py-1.5 text-[11px] font-semibold text-white transition"
                      >
                        Get Quote
                      </button>
                    </div>

                    {/* Talk Directly CTA */}
                    <button
                      type="button"
                      onClick={() => handleWhatsAppVolume()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-xs sm:text-sm font-bold text-white shadow-md hover:bg-emerald-500 transition"
                    >
                      <FaWhatsapp className="text-base" />
                      <span>Talk Directly with our Specialist</span>
                    </button>
                  </div>
                ) : (
                  <div className="mt-3.5 space-y-3">
                    <div className="rounded-2xl bg-white/5 p-4 border border-white/10">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white/60">
                        Starting Investment
                      </span>
                      <p className="mt-0.5 text-xl font-bold text-[#ff8ca0]">
                        {currentService.starting_price || "Custom Quote"}
                      </p>
                      <p className="mt-1 text-xs text-white/60">
                        Custom tailored packages with complete project accountability.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleWhatsApp()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-xs sm:text-sm font-bold text-white shadow-md hover:bg-emerald-500 transition"
                    >
                      <FaWhatsapp className="text-base" />
                      <span>Talk Directly with our Specialist</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ KEY FEATURES & DELIVERABLES ═══ */}
      {currentService.features && currentService.features.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#bc1535]">
              Deliverables & Capabilities
            </span>
            <h2 className="dm-serif mt-2 text-2xl font-light tracking-tight text-[#161310] sm:text-3xl">
              What We <em className="font-normal italic text-[#bc1535]">Provide</em>
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-[#161310]/65">
              Comprehensive execution handled end-to-end by our specialized in-house team.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {currentService.features.map((feat, idx) => (
              <div
                key={idx}
                className="group rounded-2xl border border-[#161310]/10 bg-[#fcfaf7] p-4 hover:border-[#bc1535] hover:shadow-lg transition flex flex-col justify-between"
              >
                <div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#bc1535]/10 text-[#bc1535] mb-2 group-hover:bg-[#bc1535] group-hover:text-white transition">
                    <LuSparkles className="text-lg" />
                  </div>
                  <h4 className="font-bold text-sm text-[#161310] mb-1.5">{feat.title}</h4>
                  <p className="text-xs leading-relaxed text-[#161310]/70">{feat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ═══ PACKAGES & PRICING TIERS (ONLY FOR WEBSITE DEVELOPMENT) ═══ */}
      {isWebsiteService && websitePackages && websitePackages.length > 0 && (
        <section className="bg-white py-8 sm:py-10 border-y border-[#161310]/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-6">
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#bc1535]">
                Transparent Options
              </span>
              <h2 className="dm-serif mt-2 text-2xl font-light tracking-tight text-[#161310] sm:text-3xl md:text-4xl">
                Website Development <em className="font-normal italic text-[#bc1535]">Packages</em>
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-[#161310]/65">
                Select the website package that best matches your immediate business goals.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {websitePackages.map((pkg, idx) => (
                <div
                  key={idx}
                  className="flex flex-col justify-between rounded-3xl border border-[#161310]/15 bg-[#fcfaf7] p-5 sm:p-6 shadow-sm hover:border-[#bc1535] hover:shadow-xl transition relative overflow-hidden"
                >
                  {pkg.badge && (
                    <div className="absolute top-4 right-4 rounded-full bg-[#bc1535] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                      {pkg.badge}
                    </div>
                  )}

                  <div>
                    <h3 className="font-bold text-base text-[#161310] pr-16">{pkg.name}</h3>

                    <div className="mt-3 flex items-baseline gap-1.5">
                      <span className="dm-serif text-2xl sm:text-3xl font-bold text-[#bc1535]">
                        {pkg.price}
                      </span>
                      {pkg.unit && (
                        <span className="text-xs text-[#161310]/60 font-medium">/{pkg.unit}</span>
                      )}
                    </div>

                    <p className="mt-2 text-xs leading-relaxed text-[#161310]/75">
                      {pkg.description}
                    </p>

                    {pkg.inclusions && pkg.inclusions.length > 0 && (
                      <div className="mt-3 border-t border-[#161310]/10 pt-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#161310]/60 block mb-2.5">
                          What is Included:
                        </span>
                        <ul className="space-y-2 text-xs text-[#161310]/80">
                          {pkg.inclusions.map((item, iIdx) => {
                            const text = typeof item === "string" ? item : item?.item || "";
                            return (
                              <li key={iIdx} className="flex items-start gap-2">
                                <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                                  <LuCheck className="text-[8px]" />
                                </span>
                                <span>{text}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="mt-5 pt-3 border-t border-[#161310]/10 space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPackage(pkg);
                        setInquiryModalOpen(true);
                      }}
                      className="w-full rounded-xl bg-[#bc1535] py-2.5 text-xs font-bold text-white shadow hover:bg-[#a10f2b] transition"
                    >
                      Inquire About This Package
                    </button>

                    <button
                      type="button"
                      onClick={() => handleWhatsApp(pkg.name)}
                      className="w-full flex items-center justify-center gap-1.5 rounded-xl border border-[#161310]/15 bg-white py-2 text-xs font-semibold text-[#161310] hover:bg-[#f5efe8] transition"
                    >
                      <FaWhatsapp className="text-emerald-600" />
                      <span>Book on WhatsApp</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ EXPLORE 4 RELEVANT SERVICES ═══ */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 border-t border-[#161310]/10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-5 gap-3">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#bc1535]">
              Explore Complementary Suite
            </span>
            <h2 className="dm-serif mt-1 text-2xl font-light tracking-tight text-[#161310] sm:text-3xl">
              Relevant <em className="font-normal italic text-[#bc1535]">Services</em>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {otherServicesList.map((srv) => {
            const isInfluencer = srv.slug === "influencer" || srv.slug === "influencer-marketing";
            const targetUrl = isInfluencer ? "/influencer-marketing" : `/digital-marketing/services/${srv.slug}`;
            return (
              <Link
                key={srv.slug}
                to={targetUrl}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="group flex flex-col justify-between rounded-2xl border border-[#161310] bg-[#161310] p-4 text-left transition hover:border-[#bc1535] hover:shadow-lg no-underline"
              >
              <div>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#bc1535] text-white mb-2.5 transition group-hover:bg-[#d31a40]">
                  <ServiceIcon iconKey={srv.icon} className="text-base" />
                </div>
                <h4 className="font-bold text-xs sm:text-sm text-white group-hover:text-[#ff8ca0] transition">
                  {srv.title}
                </h4>
                <p className="mt-1 text-[11px] text-white/60 line-clamp-2 leading-relaxed">
                  {srv.short_description}
                </p>
              </div>

              <div className="mt-3 pt-2 border-t border-white/10 flex items-center justify-between text-[10px] font-bold text-[#ff8ca0]">
                <span>{srv.starting_price || "Explore"}</span>
                <FaArrowRight className="text-[8px] transform group-hover:translate-x-1 transition" />
              </div>
            </Link>
          );
        })}
        </div>
      </section>

      {/* ═══ INQUIRY MODAL ═══ */}
      <AnimatePresence>
        {inquiryModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#161310]/70 p-3 sm:p-4 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-[#fcfaf7] shadow-2xl border border-[#161310]/15"
            >
              <div className="bg-[#161310] px-6 py-4 text-white flex items-center justify-between">
                <div>
                  <span className="text-[9px] font-bold uppercase tracking-wider text-[#ff8ca0]">
                    Inquiry &amp; Callback
                  </span>
                  <h3 className="dm-serif text-lg font-normal text-[#f5efe8]">
                    {selectedPackage
                      ? selectedPackage.name
                      : activeVolumeConfig
                      ? `${currentService.title} (${formatRupees(volumeQty)} ${activeVolumeConfig.unit})`
                      : isWebsiteService
                      ? websitePackages[selectedWebsitePkgIndex]?.name
                      : currentService.title}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setInquiryModalOpen(false)}
                  className="rounded-full p-1.5 text-white/60 hover:bg-white/10 hover:text-white transition"
                >
                  <LuX className="text-lg" />
                </button>
              </div>

              <div className="p-4 sm:p-5">
                {submitted ? (
                  <div className="text-center py-5 space-y-3">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                      <LuCheck className="text-2xl" />
                    </div>
                    <h4 className="font-bold text-base text-[#161310]">Request Submitted!</h4>
                    <p className="text-xs text-[#161310]/70 max-w-xs mx-auto">
                      Our marketing specialist will reach out to you within 2 hours to finalize
                      deliverables and pricing.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSubmitted(false);
                        setInquiryModalOpen(false);
                      }}
                      className="mt-4 rounded-xl bg-[#bc1535] px-6 py-2 text-xs font-bold text-white shadow"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleInquirySubmit} className="space-y-3">
                    <div>
                      <label className="block text-[11px] font-bold text-[#161310] mb-1">
                        Your Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Rahul Sharma"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none focus:border-[#bc1535]"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-[#161310] mb-1">
                          Phone / WhatsApp Number *
                        </label>
                        <input
                          type="tel"
                          required
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none focus:border-[#bc1535]"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-[#161310] mb-1">
                          Brand / Business Name
                        </label>
                        <input
                          type="text"
                          placeholder="Your Brand / Cafe"
                          value={formData.brandName}
                          onChange={(e) =>
                            setFormData({ ...formData, brandName: e.target.value })
                          }
                          className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none focus:border-[#bc1535]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-[#161310] mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        placeholder="you@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none focus:border-[#bc1535]"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-[#161310] mb-1">
                        Requirements or Notes
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Tell us what you need (e.g. shoot dates, target city, specific requirements)..."
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full rounded-xl border border-[#161310]/15 bg-white px-3 py-2 text-xs font-medium text-[#161310] outline-none focus:border-[#bc1535]"
                      />
                    </div>

                    <div className="mt-4 pt-2 space-y-2">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full rounded-xl bg-[#bc1535] py-2.5 text-xs font-bold text-white shadow-md hover:bg-[#a10f2b] transition disabled:opacity-60"
                      >
                        {isSubmitting ? "Submitting..." : "Submit & Request Callback"}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          if (selectedPackage) {
                            handleWhatsApp(selectedPackage.name);
                          } else if (activeVolumeConfig) {
                            handleWhatsAppVolume();
                          } else if (isWebsiteService && websitePackages[selectedWebsitePkgIndex]) {
                            handleWhatsApp(websitePackages[selectedWebsitePkgIndex]?.name);
                          } else {
                            handleWhatsApp();
                          }
                        }}
                        className="w-full flex items-center justify-center gap-2 rounded-xl border border-[#161310]/15 bg-white py-2 text-xs font-semibold text-[#161310] hover:bg-[#f5efe8] transition"
                      >
                        <FaWhatsapp className="text-emerald-600 text-sm" />
                        <span>Or Chat Instantly on WhatsApp</span>
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default DMServiceDetail;
